import { useCallback } from 'react'
import { useCacheSlice, useCacheMutations } from '../hooks'
import { useApi } from '@/shared/utils/request'
import { toast } from 'sonner'
import { logger } from '@/shared/utils/logger'

// Re-export pure data from cycle-free module
export type { Settings } from './settings.types'
export { defaultSettings, parseSettings } from './settings.types'

import type { Settings } from './settings.types'

export function useSettingsCache() {
  const { data, isLoading } = useCacheSlice('settings')
  const { optimisticUpdate, refresh } = useCacheMutations()
  const { makeApiRequest } = useApi()

  const settings = data as Settings

  const updateSettings = useCallback(
    async (patch: Partial<Settings>) => {
      const merged = { ...settings, ...patch }
      const rollback = optimisticUpdate('settings', () => merged)

      try {
        await makeApiRequest({
          endpoint: '/settings',
          method: 'POST',
          body: merged,
        })
        toast.success('Settings saved', { duration: 2000 })
      } catch (e) {
        rollback()
        if (e instanceof Error) {
          logger.captureException(e, { action: 'updateSettings' })
        }
        toast.error('Failed to save settings', { duration: 5000 })
      }
    },
    [settings, optimisticUpdate, makeApiRequest],
  )

  const refreshSettings = useCallback(
    () => refresh('settings'),
    [refresh],
  )

  return { settings, isLoading, updateSettings, refreshSettings }
}
